import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { confirmationDialogEventName, } from './confirmation-dialog-event';
import { GenericDialog } from './generic-dialog';
import { useModalsDialogsContext } from './modals-dialogs-context';
/**
 * Monotonic counter used to give every confirmation dialog a unique id. The legacy `showDialog`
 * helpers gave each dialog its own UUID so several could stack and close independently; reusing a
 * single id (as a shared `useGenericDialog` instance would) makes `hideModalDialog` close the wrong
 * one when two are open at once.
 */
let confirmationDialogCounter = 0;
/**
 * Listens for the native browser confirmation-dialog `CustomEvent` and renders the requested
 * dialog through the modal/dialog context, settling the dispatcher's promise with the user's
 * choice (`'OK'` on confirm, `'CANCEL'` on cancel/close).
 *
 * Mount this **exactly once**, as a child of the root `ModalDialogProvider`, so any code can
 * request a confirmation dialog via `dispatchConfirmationDialog` without wiring anything up.
 * It must not be nested inside per-modal providers, otherwise a single event would open
 * duplicate dialogs. Returns no UI itself — the dialog is rendered by the modal/dialog context.
 */
export const ConfirmationDialogListener = () => {
    const { showModalDialog, hideModalDialog } = useModalsDialogsContext();
    React.useEffect(() => {
        const handleConfirmationDialog = (event) => {
            const { resolve, title, description, confirmText, cancelText, variant, hideCancelButton, image, children } = event.detail;
            const id = `confirmation-dialog-${(confirmationDialogCounter += 1)}`;
            const descriptionId = `${id}-description`;
            const settle = (choice) => {
                resolve(choice);
                hideModalDialog(id);
            };
            showModalDialog({
                id,
                type: 'dialog',
                'aria-describedby': descriptionId,
                element: (_jsx(GenericDialog, { title: title, description: description, descriptionId: descriptionId, confirmText: confirmText, cancelText: cancelText, variant: variant, hideCancelButton: hideCancelButton, image: image, onConfirm: () => settle('OK'), onClose: () => settle('CANCEL'), children: children })),
            });
        };
        window.addEventListener(confirmationDialogEventName, handleConfirmationDialog);
        return () => {
            window.removeEventListener(confirmationDialogEventName, handleConfirmationDialog);
        };
    }, [hideModalDialog, showModalDialog]);
    return null;
};
